import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { languageNames } from '@/lib/i18n/languages';
import type { QuoteSendConfirmationReview as Review } from './quoteSendReviewContract';

/**
 * What a `send_quote` confirmation will send (#2546 lane B): the exact
 * channel, recipient and covering message, the language of the email and PDF
 * (#3633), the reviewed quote version and its totals, and what "done" will and
 * will not mean.
 */
export function QuoteSendConfirmationReview({ review }: { review: Review }) {
  const { t, i18n } = useTranslation('assistant');
  const q = review.quote;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const money = (cents: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100);
  const date = (value: string) =>
    new Intl.DateTimeFormat(locale, { timeZone: q.workshopTimezone, dateStyle: 'medium' }).format(
      new Date(value)
    );
  const time = (value: string) =>
    new Intl.DateTimeFormat(locale, { timeZone: q.workshopTimezone, timeStyle: 'short' }).format(
      new Date(value)
    );
  const row = (label: string, value: string, testId?: string) => (
    <Typography
      variant="body2"
      sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <strong>{label}: </strong>
      {value}
    </Typography>
  );

  return (
    <Box
      sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
      data-testid="quote-send-review"
    >
      <Typography variant="subtitle2">{t('quoteSend.title')}</Typography>
      {row(t('quoteSend.quote'), `${q.workOrderNumber} (${q.registrationNumber})`)}
      {row(t('quoteSend.channel'), t(`quoteSend.channels.${review.channel}`))}
      {row(
        t('quoteSend.recipient'),
        `${review.recipient} (${t(
          review.recipientSource === 'on_file'
            ? 'quoteSend.recipientOnFile'
            : 'quoteSend.recipientEntered'
        )})`,
        'quote-send-recipient'
      )}
      {row(
        t('quoteSend.message'),
        review.message ?? t('quoteSend.noMessage'),
        'quote-send-message'
      )}
      {row(t('quoteSend.language'), languageNames[review.language], 'quote-send-language')}
      {row(
        t('quoteSend.version'),
        t('quoteSend.versionValue', {
          date: date(q.updatedAt),
          time: time(q.updatedAt),
          revision: q.quoteRevision.slice('sha256:'.length, 'sha256:'.length + 12),
        }),
        'quote-send-version'
      )}
      {row(
        t('quoteSend.validUntil'),
        q.quoteExpiresAt ? date(q.quoteExpiresAt) : t('quoteSend.noExpiry')
      )}
      {row(t('quoteSend.net'), money(q.netSubtotalCents))}
      {row(
        t('quoteSend.tax', {
          vatPercent: new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(
            q.defaultVatPercent
          ),
        }),
        money(q.taxCents)
      )}
      {row(t('quoteSend.gross'), money(q.grossTotalCents), 'quote-send-gross')}
      <Typography variant="body2" color="text.secondary">
        {t('quoteSend.delivers')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('quoteSend.notApproval')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('quoteSend.tracking')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('quoteSend.changed')}
      </Typography>
    </Box>
  );
}
